import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { Icon, type IconName } from '@/components/ui/Icon';
import { Label } from '@/components/ui/Label';
import type { Publication, PublicationKind } from '@/content/publications';
import { cn } from '@/lib/cn';

const ICONS: Record<PublicationKind, IconName> = {
  episode: 'play',
  releve: 'rss',
  note: 'doc',
};

type PublicationRowProps = {
  publication: Publication;
  /** Pictogramme du type devant la catégorie (index du blog). */
  withIcon?: boolean;
  /** Ligne de détail sous le titre (index du blog). */
  withDetail?: boolean;
};

/**
 * Ligne de liste : date, catégorie, titre, flèche. Sous 1100 px la catégorie
 * disparaît, sous 720 px la date aussi (maquette, `.row`).
 */
export function PublicationRow({ publication, withIcon, withDetail }: PublicationRowProps) {
  const { to } = publication;
  const body: ReactNode = (
    <>
      <Label className="hidden tab:block">{publication.date}</Label>
      <Label className="hidden items-center gap-2 desk:inline-flex">
        {withIcon && <Icon name={ICONS[publication.kind]} size={15} />}
        {publication.category}
      </Label>
      <span>
        <span className="block text-h4">{publication.title}</span>
        {withDetail && publication.detail && (
          <span className="mt-1 block max-w-[46em] text-body-xs text-encre-soft">
            {publication.detail}
          </span>
        )}
      </span>
      <span
        aria-hidden="true"
        className="text-encre-mute transition-transform group-hover:translate-x-1 group-hover:text-encre"
      >
        {to ? '→' : ''}
      </span>
    </>
  );

  const classes = cn(
    'group grid items-baseline gap-6 border-b border-line py-5.5 text-encre no-underline',
    'grid-cols-[minmax(0,1fr)_24px] tab:grid-cols-[110px_minmax(0,1fr)_24px]',
    'desk:grid-cols-[130px_150px_minmax(0,1fr)_24px]',
    to && 'hover:bg-linear-to-r hover:from-blanc hover:to-transparent',
  );

  return to ? (
    <Link to={to} className={classes}>
      {body}
    </Link>
  ) : (
    <div className={classes}>{body}</div>
  );
}

/** Liste des publications, filet encre au-dessus (maquette, `.list`). */
export function PublicationList({ children }: { children: ReactNode }) {
  return <div className="border-t border-encre">{children}</div>;
}
